import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaChevronDown,
  FaEuroSign,
  FaFilter,
  FaLayerGroup,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

import useScrollToTop from "../../../hooks/useScrollToTop";
import Reveal from "../../../components/Reveal/Reveal";
import ScrollIndicator from "../../../components/ScrollIndicator/ScrollIndicator";

import Footer from "../HomePage/Footer";
import GroupHorairesTarifs from "./GroupHorairesTarifs";
import WeeklySchedule from "./WeeklySchedule";

import { getGroupsHome } from "../../../services/groupServices";

import "../HomePage/Home.css";

const tokenizeRanges = (text) =>
  String(text || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const rangeSort = (a, b) => {
  const regex = /^(\d{4})\s*[-/]\s*(\d{4})$/;
  const matchA = a.match(regex);
  const matchB = b.match(regex);

  if (matchA && matchB) {
    return Number(matchB[1]) - Number(matchA[1]);
  }

  const numberA =
    (a.match(/\d+/g) || []).map(Number).sort((x, y) => y - x)[0] || -1;

  const numberB =
    (b.match(/\d+/g) || []).map(Number).sort((x, y) => y - x)[0] || -1;

  if (numberA !== numberB) {
    return numberB - numberA;
  }

  return a.localeCompare(b, "fr");
};

const normalizeText = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const isActiveGroup = (group) => {
  if (
    group?.is_active === undefined ||
    group?.is_active === null ||
    group?.is_active === ""
  ) {
    return true;
  }

  return (
    group.is_active === true || group.is_active === 1 || group.is_active === "1"
  );
};

const getGroupSearchText = (group) =>
  normalizeText(
    [
      group?.nom,
      group?.annees_naissance,
      group?.horaire1,
      group?.horaire2,
      group?.enseignant1,
      group?.enseignant2,
      group?.description,
    ]
      .filter(Boolean)
      .join(" "),
  );

const countScheduleSlots = (groups) =>
  groups.reduce((total, group) => {
    return total + [group?.horaire1, group?.horaire2].filter(Boolean).length;
  }, 0);

export default function HorairesTarifsPage() {
  useScrollToTop();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [activeRange, setActiveRange] = useState("Tous");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchGroups = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const response = await getGroupsHome();

        if (!mounted) return;

        const activeGroups = (Array.isArray(response) ? response : [])
          .filter(isActiveGroup)
          .sort((a, b) => {
            const ordreA = Number(a?.ordre || a?.order || 999);
            const ordreB = Number(b?.ordre || b?.order || 999);

            return ordreA - ordreB;
          });

        setGroups(activeGroups);
      } catch (error) {
        console.error("Erreur lors de la récupération des groupes :", error);

        if (mounted) {
          setErrorMsg("Impossible de charger les groupes, horaires et tarifs.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchGroups();

    return () => {
      mounted = false;
    };
  }, []);

  const allRanges = useMemo(() => {
    const ranges = new Set();

    groups.forEach((group) => {
      tokenizeRanges(group?.annees_naissance).forEach((range) =>
        ranges.add(range),
      );
    });

    return ["Tous", ...Array.from(ranges).sort(rangeSort)];
  }, [groups]);

  const filteredGroups = useMemo(() => {
    const normalizedQuery = normalizeText(query);

    return groups.filter((group) => {
      const ranges = tokenizeRanges(group?.annees_naissance);

      const rangeMatches =
        activeRange === "Tous" || ranges.includes(activeRange);

      const queryMatches =
        !normalizedQuery || getGroupSearchText(group).includes(normalizedQuery);

      return rangeMatches && queryMatches;
    });
  }, [groups, activeRange, query]);

  const totalSlots = useMemo(() => countScheduleSlots(groups), [groups]);

  const hasActiveFilters = query.trim() || activeRange !== "Tous";

  const resetFilters = () => {
    setQuery("");
    setActiveRange("Tous");
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <main>
        <section className="relative overflow-hidden bg-slate-950 px-4 pb-16 pt-36 text-white sm:px-6 lg:px-8 lg:pb-20 lg:pt-40">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.30),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.22),transparent_32%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:44px_44px] opacity-20" />

          <div className="relative mx-auto max-w-7xl">
            <Reveal className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-white/70 backdrop-blur-xl">
                Horaires & tarifs
              </div>

              <h1 className="mt-6 font-barlow text-5xl font-bold leading-tight tracking-tight text-white sm:text-6xl lg:text-7xl">
                Trouvez le groupe
                <br />
                adapté à votre pratique
              </h1>

              <p className="mt-6 max-w-2xl text-base leading-8 text-white/75 sm:text-lg">
                Consultez les groupes, les années de naissance, les enseignants,
                les horaires de cours et les tarifs annuels.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="relative overflow-hidden bg-slate-50 py-10 dark:bg-slate-950">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.10),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.08),transparent_32%)]" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal className="grid gap-4 md:grid-cols-3">
              <div className="rounded-[2rem] border border-slate-200 bg-white/85 p-6 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
                <FaLayerGroup className="mb-4 text-2xl text-primary" />
                <p className="font-barlow text-4xl font-bold text-slate-950 dark:text-white">
                  {groups.length}
                </p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                  Groupe{groups.length > 1 ? "s" : ""}
                </p>
              </div>

              <div className="rounded-[2rem] border border-slate-200 bg-white/85 p-6 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
                <FaCalendarAlt className="mb-4 text-2xl text-primary" />
                <p className="font-barlow text-4xl font-bold text-slate-950 dark:text-white">
                  {totalSlots}
                </p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                  Créneau{totalSlots > 1 ? "x" : ""}
                </p>
              </div>

              <div className="rounded-[2rem] border border-slate-200 bg-white/85 p-6 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
                <FaEuroSign className="mb-4 text-2xl text-primary" />
                <p className="font-barlow text-4xl font-bold text-slate-950 dark:text-white">
                  Tarifs
                </p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                  Licence + cotisation
                </p>
              </div>
            </Reveal>

            {errorMsg && (
              <Reveal className="mt-8">
                <div className="rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-center text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                  {errorMsg}
                </div>
              </Reveal>
            )}
          </div>
        </section>

        <div className="sticky top-[96px] z-[45] border-y border-slate-200/80 bg-slate-50/90 py-3 backdrop-blur-2xl dark:border-white/10 dark:bg-slate-950/90">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-[2rem] border border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95">
              <div className="grid gap-4 lg:grid-cols-[1fr_320px_auto] lg:items-center">
                <div className="relative">
                  <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                  <input
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Rechercher un groupe, un enseignant, un horaire..."
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3.5 pl-11 pr-4 text-sm font-medium text-slate-900 outline-none transition focus:border-primary focus:bg-white focus:ring-4 focus:ring-orange-500/10 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500"
                  />
                </div>

                <div className="relative">
                  <label
                    htmlFor="range-filter"
                    className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-400 lg:hidden"
                  >
                    <FaFilter className="text-primary" />
                    Filtrer par tranche d’âge
                  </label>

                  <FaFilter className="pointer-events-none absolute left-4 top-1/2 hidden -translate-y-1/2 text-primary lg:block" />

                  <select
                    id="range-filter"
                    value={activeRange}
                    onChange={(event) => setActiveRange(event.target.value)}
                    className="w-full appearance-none rounded-2xl border border-slate-200 bg-slate-50 py-3.5 pl-4 pr-11 text-sm font-bold text-slate-800 outline-none transition focus:border-primary focus:bg-white focus:ring-4 focus:ring-orange-500/10 dark:border-white/10 dark:bg-white/5 dark:text-white lg:pl-11"
                  >
                    {allRanges.map((range) => (
                      <option key={range} value={range}>
                        {range === "Tous" ? "Toutes les tranches d’âge" : range}
                      </option>
                    ))}
                  </select>

                  <FaChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-400" />
                </div>

                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
                  >
                    <FaTimes />
                    Réinitialiser
                  </button>
                )}
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                <p>
                  <span className="font-bold text-slate-950 dark:text-white">
                    {filteredGroups.length}
                  </span>{" "}
                  résultat{filteredGroups.length > 1 ? "s" : ""} affiché
                  {filteredGroups.length > 1 ? "s" : ""}
                </p>

                <p className="hidden text-xs font-medium text-slate-400 sm:block">
                  Les filtres restent accessibles pendant la consultation.
                </p>
              </div>
            </div>
          </div>
        </div>

        <GroupHorairesTarifs groups={filteredGroups} loading={loading} />

        <WeeklySchedule groups={filteredGroups} loading={loading} />

        <Footer />
        <ScrollIndicator />
      </main>
    </div>
  );
}
